import { useState } from "react";
import { useLocation, Link } from "react-router-dom";
import { CheckCircle2, Loader2, MailPlus } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { api, errorMessage } from "@/lib/api";
import { trackEvent } from "@/lib/analytics";
import { useFormStart } from "@/lib/useFormStart";
import { BUSINESS, COMMUNITY_OFFER_ACTIVE, ONLINE_FORMS_ENABLED } from "@/config/site";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
const PHONE_RE = /^[+0-9()\-\s]{9,20}$/;
const inputCls = "h-11 rounded-xl bg-white text-[#25332B] placeholder:text-[#7b8780] border-[rgba(40,91,69,0.22)] focus-visible:ring-2 focus-visible:ring-[hsl(var(--ring))]";

export const SignupForm = ({ idPrefix = "signup", dark = false }) => {
  const location = useLocation();
  const startRef = useFormStart();
  const [form, setForm] = useState({ first_name: "", email: "", phone: "", marketing_consent: false, website: "" });
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  if (!ONLINE_FORMS_ENABLED) {
    return (
      <div className={`space-y-3 rounded-xl p-5 ${dark ? "bg-white/10 text-white" : "bg-[#F1F6F0]"}`} role="status">
        <p>Community sign-up will be available soon. Please email us with any product enquiries.</p>
        <a className="font-semibold underline underline-offset-4" href={`mailto:${BUSINESS.email}`}>{BUSINESS.email}</a>
      </div>
    );
  }

  const set = (k, v) => {
    setForm((f) => ({ ...f, [k]: v }));
    if (errors[k]) setErrors((e) => ({ ...e, [k]: undefined }));
  };

  const onSubmit = async (ev) => {
    ev.preventDefault();
    const e = {};
    if (!form.first_name.trim()) e.first_name = "Please enter your first name.";
    if (!EMAIL_RE.test(form.email.trim())) e.email = "Please enter a valid email address.";
    if (form.phone.trim() && !PHONE_RE.test(form.phone.trim())) e.phone = "Please enter a valid phone number.";
    if (!form.marketing_consent) e.marketing_consent = "Please tick the box to agree to receive updates by email.";
    setErrors(e);
    if (Object.keys(e).length) return;
    setSubmitting(true);
    try {
      const res = await api.post("/subscribers", {
        first_name: form.first_name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim() || null,
        marketing_consent: form.marketing_consent,
        source_page: location.pathname,
        website: form.website,
        started_at: startRef.current,
      });
      trackEvent("newsletter_subscribe", { status: res.data?.status });
      setResult(res.data);
    } catch (err) {
      const msg = errorMessage(err, "We could not complete your signup. Please try again.");
      toast.error(msg);
      setErrors({ _form: msg });
    } finally {
      setSubmitting(false);
    }
  };

  const labelCls = dark ? "text-[#FAF9F5]" : "";
  const mutedCls = dark ? "text-[#dfe9dc]" : "text-muted-foreground";

  if (result) {
    return (
      <div className={`rounded-2xl p-6 ${dark ? "bg-white/10" : "bg-[#F1F6F0]"}`} role="status" data-testid={`${idPrefix}-success`}>
        <CheckCircle2 className={`h-7 w-7 ${dark ? "text-[#cfe2c9]" : "text-[color:var(--u-green)]"}`} aria-hidden="true" />
        <p className={`mt-3 text-lg font-semibold ${labelCls}`}>{result.status === "already_subscribed" ? "You’re already subscribed" : "Almost there — check your inbox"}</p>
        <p className={`mt-1 text-sm leading-relaxed ${mutedCls}`}>{result.message}</p>
      </div>
    );
  }

  return (
    <form noValidate onSubmit={onSubmit} className="space-y-4" data-testid={`${idPrefix}-form`}>
      <div aria-hidden="true" style={{ position: "absolute", left: "-10000px", width: 1, height: 1, overflow: "hidden" }}>
        <label htmlFor={`${idPrefix}-website`}>Website</label>
        <input id={`${idPrefix}-website`} tabIndex={-1} autoComplete="off" value={form.website} onChange={(e) => set("website", e.target.value)} />
      </div>
      <div className="grid gap-4 sm:grid-cols-2">
        <div className="space-y-1.5">
          <Label htmlFor={`${idPrefix}-first-name`} className={`text-sm font-semibold ${labelCls}`}>
            First name
          </Label>
          <Input id={`${idPrefix}-first-name`} autoComplete="given-name" className={inputCls} value={form.first_name} onChange={(e) => set("first_name", e.target.value)} aria-invalid={errors.first_name ? "true" : "false"} aria-describedby={errors.first_name ? `${idPrefix}-first-name-error` : undefined} data-testid={`${idPrefix}-first-name-input`} maxLength={60} />
          {errors.first_name ? <p id={`${idPrefix}-first-name-error`} role="alert" className={`text-xs font-medium ${dark ? "text-[#ffd7d2]" : "text-[#B42318]"}`}>{errors.first_name}</p> : null}
        </div>
        <div className="space-y-1.5">
          <Label htmlFor={`${idPrefix}-email`} className={`text-sm font-semibold ${labelCls}`}>
            Email address
          </Label>
          <Input id={`${idPrefix}-email`} type="email" autoComplete="email" className={inputCls} value={form.email} onChange={(e) => set("email", e.target.value)} aria-invalid={errors.email ? "true" : "false"} aria-describedby={errors.email ? `${idPrefix}-email-error` : undefined} data-testid={`${idPrefix}-email-input`} maxLength={120} />
          {errors.email ? <p id={`${idPrefix}-email-error`} role="alert" className={`text-xs font-medium ${dark ? "text-[#ffd7d2]" : "text-[#B42318]"}`}>{errors.email}</p> : null}
        </div>
      </div>
      <div className="space-y-1.5">
        <Label htmlFor={`${idPrefix}-phone`} className={`text-sm font-semibold ${labelCls}`}>
          Phone number <span className={`font-normal ${mutedCls}`}>(optional)</span>
        </Label>
        <Input id={`${idPrefix}-phone`} type="tel" autoComplete="tel" className={inputCls} value={form.phone} onChange={(e) => set("phone", e.target.value)} aria-invalid={errors.phone ? "true" : "false"} data-testid={`${idPrefix}-phone-input`} maxLength={20} />
        {errors.phone ? <p role="alert" className={`text-xs font-medium ${dark ? "text-[#ffd7d2]" : "text-[#B42318]"}`}>{errors.phone}</p> : null}
      </div>
      <div className="space-y-1.5">
        <div className={`flex items-start gap-3 rounded-xl p-3.5 ${dark ? "bg-white/10" : "bg-[#FAF9F5] border"}`}>
          <Checkbox id={`${idPrefix}-consent`} checked={form.marketing_consent} onCheckedChange={(v) => set("marketing_consent", v === true)} className={`mt-0.5 ${dark ? "border-[#FAF9F5] data-[state=checked]:bg-[#FAF9F5] data-[state=checked]:text-[#285B45]" : ""}`} data-testid={`${idPrefix}-consent-checkbox`} aria-invalid={errors.marketing_consent ? "true" : "false"} />
          <Label htmlFor={`${idPrefix}-consent`} className={`text-sm font-normal leading-relaxed ${dark ? "text-[#eef4ec]" : "text-[#3d4a42]"}`}>
            I agree to receive product news, updates and promotional emails from Uhlumo Organics. I can unsubscribe at any time. See our{" "}
            <Link to="/privacy-policy" className={dark ? "font-semibold underline underline-offset-4" : "link-accent"}>
              privacy notice
            </Link>
            .
          </Label>
        </div>
        {errors.marketing_consent ? <p role="alert" className={`text-xs font-medium ${dark ? "text-[#ffd7d2]" : "text-[#B42318]"}`}>{errors.marketing_consent}</p> : null}
      </div>
      {COMMUNITY_OFFER_ACTIVE ? <p className={`text-xs ${mutedCls}`}>First-purchase offer subject to published terms and conditions.</p> : null}
      <button type="submit" className={`btn w-full sm:w-auto sm:px-8 ${dark ? "btn-light" : "btn-primary"}`} disabled={submitting} data-testid={`${idPrefix}-submit-button`}>
        {submitting ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> : <MailPlus className="h-4 w-4" aria-hidden="true" />}
        {submitting ? "Joining…" : "Join the Community"}
      </button>
    </form>
  );
};
